(() => {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const SOURCE_WIDTH = 1536;
  const SOURCE_HEIGHT = 1024;
  const OUTPUT_WIDTH = 1010;
  const OUTPUT_HEIGHT = 600;
  const FRAME_INTERVAL = 1000 / 30;
  const FLOW_PERIOD = 6500;
  const STATIC_RED = [255, 0, 0];
  const FLOW_SELECTOR = '[data-hub-banner-flow="sill"], [data-hub-banner-flow="badge"]';

  const CONFIG = Object.freeze({
    sill: Object.freeze({
      crop: [300, 230, 1010, 600],
      sourceNames: Object.freeze(['lds-dodge-srt-solid-red-side.jpg', 'dodge-srt-solid-red-side.jpg']),
      edgePath: Object.freeze([
        [372, 274],
        [347, 313],
        [350, 329],
        [1197, 774],
        [1223, 778],
        [1267, 752],
        [1266, 727],
      ]),
      barPaths: Object.freeze([
        Object.freeze([[443, 343], [646, 444]]),
        Object.freeze([[868, 558], [1124, 696]]),
      ]),
      glyphPolygon: Object.freeze([
        [682, 450],
        [845, 505],
        [836, 544],
        [674, 491],
      ]),
      // The geometric limits describe only the real artwork/edge zones. They
      // replace the former broad plate polygon, which also caught yellow body
      // bounce as if it were an LED surface.
    }),
    badge: Object.freeze({
      crop: [280, 235, 980, 582],
      sourceNames: Object.freeze(['lds-dodge-hub-badge-installed.jpg', 'dodge-challenger-badge-installed.jpg']),
      letterRoi: Object.freeze([405, 704, 1115, 749]),
      haloRoi: Object.freeze([390, 696, 1130, 757]),
    }),
  });

  const states = new Map();
  const statesByRoot = new WeakMap();
  const activeStates = new Set();
  let schedulerFrame = null;
  let flowColorsTime = -1;
  const flowColors = {
    red: new Uint8Array(OUTPUT_WIDTH),
    green: new Uint8Array(OUTPUT_WIDTH),
    blue: new Uint8Array(OUTPUT_WIDTH),
  };

  const clampByte = value => Math.max(0, Math.min(255, Math.round(value)));
  const smoothstep = (edge0, edge1, value) => {
    if (edge0 === edge1) return value < edge0 ? 0 : 1;
    const t = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
  };
  const flowHue = (h, n) => {
    const k = (n + h * 12) % 12;
    return Math.round(255 * (0.57 - 0.43 * Math.max(-1, Math.min(k - 3, 9 - k, 1))));
  };
  const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  const hoverFine = () => {
    return window.matchMedia?.('(hover: hover) and (pointer: fine)').matches === true;
  };
  const isDocumentHidden = () => document.hidden === true;

  const setStatus = (state, message) => {
    if (state.status) state.status.textContent = message;
  };

  const basenameFor = source => {
    if (!source) return '';
    try {
      const url = new URL(source, document.baseURI);
      const pathname = decodeURIComponent(url.pathname || '');
      return pathname.slice(pathname.lastIndexOf('/') + 1).toLowerCase();
    } catch (_error) {
      const clean = String(source).split(/[?#]/, 1)[0];
      return clean.slice(clean.lastIndexOf('/') + 1).toLowerCase();
    }
  };

  const sourceMatches = (kind, source) => {
    const name = basenameFor(source);
    return CONFIG[kind].sourceNames.includes(name);
  };

  const pointInPolygon = (x, y, points) => {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i, i += 1) {
      const [xi, yi] = points[i];
      const [xj, yj] = points[j];
      const intersects = (yi > y) !== (yj > y)
        && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersects) inside = !inside;
    }
    return inside;
  };

  const distanceToSegment = (x, y, start, end) => {
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    if (dx === 0 && dy === 0) return Math.hypot(x - start[0], y - start[1]);
    const lengthSquared = dx * dx + dy * dy;
    const t = Math.max(0, Math.min(1, ((x - start[0]) * dx + (y - start[1]) * dy) / lengthSquared));
    return Math.hypot(x - (start[0] + t * dx), y - (start[1] + t * dy));
  };

  const distanceToPath = (x, y, path) => {
    let distance = Infinity;
    for (let i = 1; i < path.length; i += 1) {
      distance = Math.min(distance, distanceToSegment(x, y, path[i - 1], path[i]));
    }
    return distance;
  };

  const pathBounds = path => {
    const xs = path.map(point => point[0]);
    const ys = path.map(point => point[1]);
    return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
  };

  const inBounds = (x, y, bounds, padding = 0) => (
    x >= bounds[0] - padding && x <= bounds[2] + padding
    && y >= bounds[1] - padding && y <= bounds[3] + padding
  );

  const median = values => {
    if (!values.length) return 0;
    values.sort((a, b) => a - b);
    return values[Math.floor(values.length / 2)];
  };

  const estimateWarmAmbientRatio = (sourceData, path) => {
    const bounds = pathBounds(path);
    const samples = [];
    const left = Math.max(0, Math.floor(bounds[0] - 50));
    const right = Math.min(SOURCE_WIDTH - 1, Math.ceil(bounds[2] + 50));
    const top = Math.max(0, Math.floor(bounds[1] - 50));
    const bottom = Math.min(SOURCE_HEIGHT - 1, Math.ceil(bounds[3] + 50));
    for (let y = top; y <= bottom; y += 1) {
      for (let x = left; x <= right; x += 1) {
        const distance = distanceToPath(x, y, path);
        if (distance < 38 || distance > 50) continue;
        const offset = (y * SOURCE_WIDTH + x) * 4;
        const r = sourceData.data[offset];
        const g = sourceData.data[offset + 1];
        const b = sourceData.data[offset + 2];
        if (r < 140 || g < 70 || b > g * 0.65 || r <= g * 1.15) continue;
        const ratio = r / Math.max(g, 1);
        if (ratio >= 1.25 && ratio <= 2.2) samples.push(ratio);
      }
    }
    return Math.max(1.35, Math.min(1.95, median(samples) || 1.65));
  };

  const sillRedSignal = (sourceData, sourceOffset, ambientRatio) => {
    const r = sourceData.data[sourceOffset];
    const g = sourceData.data[sourceOffset + 1];
    const b = sourceData.data[sourceOffset + 2];
    const redSignal = Math.max(0, r - 1.65 * Math.max(g, b));
    if (r <= 70 || redSignal <= 0) return null;

    const warmBody = r > 130 && g > 70 && b <= g * 0.65 && r > g * 1.15;
    const neutralRed = warmBody ? g * ambientRatio : Math.max(g, b);
    const oldRedContribution = Math.max(0, r - neutralRed);
    if (oldRedContribution <= 0.01) return null;
    return {
      redSignal,
      oldRedContribution,
      chromaStrength: smoothstep(0, 30, redSignal),
    };
  };

  const copyImageData = (ctx, imageData) => {
    const copy = ctx.createImageData(OUTPUT_WIDTH, OUTPUT_HEIGHT);
    copy.data.set(imageData.data);
    return copy;
  };

  const mappedSourcePixel = (state, x, y) => {
    const [cropX, cropY, cropWidth, cropHeight] = state.crop;
    const sourceX = Math.min(
      SOURCE_WIDTH - 1,
      Math.max(0, cropX + Math.floor((x + 0.5) * cropWidth / OUTPUT_WIDTH)),
    );
    const sourceY = Math.min(
      SOURCE_HEIGHT - 1,
      Math.max(0, cropY + Math.floor((y + 0.5) * cropHeight / OUTPUT_HEIGHT)),
    );
    return {
      x: sourceX,
      y: sourceY,
      index: (sourceY * SOURCE_WIDTH + sourceX) * 4,
    };
  };

  const outputImageData = (state, sourceImage, sourceData) => {
    const {ctx, kind, crop} = state;
    if (kind === 'sill' && crop[2] === OUTPUT_WIDTH && crop[3] === OUTPUT_HEIGHT) {
      const output = ctx.createImageData(OUTPUT_WIDTH, OUTPUT_HEIGHT);
      for (let y = 0; y < OUTPUT_HEIGHT; y += 1) {
        const sourceOffset = ((crop[1] + y) * SOURCE_WIDTH + crop[0]) * 4;
        const outputOffset = y * OUTPUT_WIDTH * 4;
        output.data.set(sourceData.data.subarray(sourceOffset, sourceOffset + OUTPUT_WIDTH * 4), outputOffset);
      }
      return output;
    }

    ctx.clearRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    ctx.drawImage(sourceImage, crop[0], crop[1], crop[2], crop[3], 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    return ctx.getImageData(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
  };

  const addMask = (flowAmounts, oldAmounts, index, flowAmount, oldAmount = flowAmount) => {
    // Flow and source-red removal have different feather radii. Keep the
    // strongest contribution for each independently so the wider removal
    // halo can be represented by a zero-flow pixel without a hard boundary.
    if (flowAmount > flowAmounts[index]) flowAmounts[index] = flowAmount;
    if (oldAmount > oldAmounts[index]) oldAmounts[index] = oldAmount;
  };

  const buildSillMask = (state, sourceData, flowAmounts, oldAmounts) => {
    const {edgePath, barPaths, glyphPolygon} = CONFIG.sill;
    const edgeBounds = pathBounds(edgePath);
    const barBounds = barPaths.map(path => pathBounds(path));
    const glyphBounds = pathBounds(glyphPolygon);
    const ambientRatio = estimateWarmAmbientRatio(sourceData, edgePath);
    const glyphCore = new Uint8Array(OUTPUT_WIDTH * OUTPUT_HEIGHT);

    // Find the actual saturated red pixels in the source glyph polygon first.
    // The later halo is dilated from these pixels, so no replacement font or
    // synthetic letter geometry can create a rainbow patch.
    for (let y = 0; y < OUTPUT_HEIGHT; y += 1) {
      for (let x = 0; x < OUTPUT_WIDTH; x += 1) {
        const mapped = mappedSourcePixel(state, x, y);
        const index = y * OUTPUT_WIDTH + x;
        const signal = sillRedSignal(sourceData, mapped.index, ambientRatio);
        if (!signal) continue;

        let flowGeometryStrength = 0;
        let removalGeometryStrength = 0;
        if (inBounds(mapped.x, mapped.y, edgeBounds, 30)) {
          const edgeDistance = distanceToPath(mapped.x, mapped.y, edgePath);
          if (edgeDistance <= 30) removalGeometryStrength = Math.max(
            removalGeometryStrength,
            edgeDistance <= 20 ? 1 : smoothstep(30, 20, edgeDistance),
          );
          if (edgeDistance <= 15) flowGeometryStrength = Math.max(
            flowGeometryStrength,
            edgeDistance <= 4 ? 1 : smoothstep(15, 4, edgeDistance),
          );
          // The photographed end caps are wider than the hand-traced center
          // path. Use the source's own bright-red pixels as the core there so
          // real LED pixels are not mistaken for the outer removal feather.
          const sourceR = sourceData.data[mapped.index];
          const sourceG = sourceData.data[mapped.index + 1];
          const sourceB = sourceData.data[mapped.index + 2];
          if (edgeDistance <= 20 && sourceR > 200 && sourceG <= 55 && sourceB <= 55) {
            flowGeometryStrength = 1;
            removalGeometryStrength = 1;
          }
        }
        for (let barIndex = 0; barIndex < barPaths.length; barIndex += 1) {
          if (!inBounds(mapped.x, mapped.y, barBounds[barIndex], 16)) continue;
          const barDistance = distanceToPath(mapped.x, mapped.y, barPaths[barIndex]);
          if (barDistance <= 16) removalGeometryStrength = Math.max(
            removalGeometryStrength,
            barDistance <= 10 ? 1 : smoothstep(16, 10, barDistance),
          );
          if (barDistance <= 10) flowGeometryStrength = Math.max(
            flowGeometryStrength,
            barDistance <= 4 ? 1 : smoothstep(10, 4, barDistance),
          );
        }

        if (inBounds(mapped.x, mapped.y, glyphBounds) && pointInPolygon(mapped.x, mapped.y, glyphPolygon)
          && signal.redSignal >= 90) {
          glyphCore[index] = 1;
        }

        if (flowGeometryStrength <= 0 && removalGeometryStrength <= 0) continue;
        const flowAmount = signal.oldRedContribution * signal.chromaStrength * flowGeometryStrength;
        const oldAmount = signal.oldRedContribution * signal.chromaStrength * removalGeometryStrength;
        if (flowAmount <= 0.01 && oldAmount <= 0.01) continue;
        addMask(flowAmounts, oldAmounts, index, flowAmount, oldAmount);
      }
    }

    // Feather source-derived removal farther than the replacement Flow glow.
    // This gives the original red excess a gradual neutral transition instead
    // of a visible cut at the replacement glow's outer edge.
    const glyphGlow = new Float32Array(OUTPUT_WIDTH * OUTPUT_HEIGHT);
    const glyphRemoval = new Float32Array(OUTPUT_WIDTH * OUTPUT_HEIGHT);
    const glyphGlowRadius = 8;
    const glyphRemovalRadius = 14;
    for (let index = 0; index < glyphCore.length; index += 1) {
      if (!glyphCore[index]) continue;
      const coreX = index % OUTPUT_WIDTH;
      const coreY = Math.floor(index / OUTPUT_WIDTH);
      for (let dy = -glyphRemovalRadius; dy <= glyphRemovalRadius; dy += 1) {
        const y = coreY + dy;
        if (y < 0 || y >= OUTPUT_HEIGHT) continue;
        for (let dx = -glyphRemovalRadius; dx <= glyphRemovalRadius; dx += 1) {
          const x = coreX + dx;
          if (x < 0 || x >= OUTPUT_WIDTH) continue;
          const distance = Math.hypot(dx, dy);
          const glowIndex = y * OUTPUT_WIDTH + x;
          if (distance <= glyphRemovalRadius) {
            const removalFeather = distance <= 8
              ? 1
              : smoothstep(glyphRemovalRadius, 8, distance);
            if (removalFeather > glyphRemoval[glowIndex]) glyphRemoval[glowIndex] = removalFeather;
          }
          if (distance <= glyphGlowRadius) {
            const flowFeather = smoothstep(glyphGlowRadius, 0, distance);
            if (flowFeather > glyphGlow[glowIndex]) glyphGlow[glowIndex] = flowFeather;
          }
        }
      }
    }
    for (let index = 0; index < glyphGlow.length; index += 1) {
      const flowGeometryStrength = glyphGlow[index];
      const removalGeometryStrength = glyphRemoval[index];
      if (flowGeometryStrength <= 0 && removalGeometryStrength <= 0) continue;
      const x = index % OUTPUT_WIDTH;
      const y = Math.floor(index / OUTPUT_WIDTH);
      const mapped = mappedSourcePixel(state, x, y);
      if (!inBounds(mapped.x, mapped.y, glyphBounds, glyphRemovalRadius)) continue;
      const signal = sillRedSignal(sourceData, mapped.index, ambientRatio);
      if (!signal) continue;
      const flowAmount = signal.oldRedContribution * signal.chromaStrength * flowGeometryStrength;
      const oldAmount = signal.oldRedContribution * signal.chromaStrength * removalGeometryStrength;
      if (flowAmount <= 0.01 && oldAmount <= 0.01) continue;
      addMask(flowAmounts, oldAmounts, index, flowAmount, oldAmount);
    }
  };

  const buildBadgeMask = (state, sourceData, flowAmounts, oldAmounts) => {
    const [roiLeft, roiTop, roiRight, roiBottom] = CONFIG.badge.letterRoi;
    const letterAmounts = new Float32Array(OUTPUT_WIDTH * OUTPUT_HEIGHT);

    for (let y = 0; y < OUTPUT_HEIGHT; y += 1) {
      for (let x = 0; x < OUTPUT_WIDTH; x += 1) {
        const mapped = mappedSourcePixel(state, x, y);
        if (mapped.x < roiLeft || mapped.x > roiRight || mapped.y < roiTop || mapped.y > roiBottom) continue;

        const sourceOffset = mapped.index;
        const r = sourceData.data[sourceOffset];
        const g = sourceData.data[sourceOffset + 1];
        const b = sourceData.data[sourceOffset + 2];
        const minimum = Math.min(r, g, b);
        const chromaDifference = Math.max(r, g, b) - minimum;
        if (minimum <= 45 || chromaDifference >= 60) continue;

        // The glyph silhouette is sourced from the actual white/gray artwork;
        // low-opacity antialiasing fades in from the same source threshold.
        const strength = smoothstep(0, 45, minimum - 45);
        const amount = minimum * strength;
        if (amount <= 0.01) continue;
        const index = y * OUTPUT_WIDTH + x;
        letterAmounts[index] = amount;
        addMask(flowAmounts, oldAmounts, index, amount, amount);
      }
    }

    const [haloLeft, haloTop, haloRight, haloBottom] = CONFIG.badge.haloRoi;
    const haloRadius = 5;
    for (let y = 0; y < OUTPUT_HEIGHT; y += 1) {
      for (let x = 0; x < OUTPUT_WIDTH; x += 1) {
        const index = y * OUTPUT_WIDTH + x;
        if (letterAmounts[index] > 0) continue;
        const mapped = mappedSourcePixel(state, x, y);
        if (mapped.x < haloLeft || mapped.x > haloRight || mapped.y < haloTop || mapped.y > haloBottom) continue;

        let haloStrength = 0;
        for (let dy = -haloRadius; dy <= haloRadius; dy += 1) {
          const yy = y + dy;
          if (yy < 0 || yy >= OUTPUT_HEIGHT) continue;
          for (let dx = -haloRadius; dx <= haloRadius; dx += 1) {
            const xx = x + dx;
            if (xx < 0 || xx >= OUTPUT_WIDTH) continue;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > haloRadius) continue;
            const nearby = letterAmounts[yy * OUTPUT_WIDTH + xx];
            if (nearby <= 0) continue;
            const falloff = 1 - distance / (haloRadius + 1);
            haloStrength = Math.max(haloStrength, (nearby / 255) * falloff * falloff);
          }
        }
        if (haloStrength <= 0) continue;

        const sourceOffset = mapped.index;
        const backgroundLevel = Math.max(
          sourceData.data[sourceOffset],
          sourceData.data[sourceOffset + 1],
          sourceData.data[sourceOffset + 2],
        );
        // Keep the glow on the dark badge strip; this also prevents it from
        // touching the plate outline or any bright surrounding bodywork.
        if (backgroundLevel >= 90) continue;
        const amount = 22 * haloStrength;
        addMask(flowAmounts, oldAmounts, index, amount, amount);
      }
    }

    return letterAmounts;
  };

  const buildMask = (state, sourceData) => {
    const amounts = new Float32Array(OUTPUT_WIDTH * OUTPUT_HEIGHT);
    const oldAmounts = new Float32Array(OUTPUT_WIDTH * OUTPUT_HEIGHT);
    const letterAmounts = state.kind === 'badge'
      ? buildBadgeMask(state, sourceData, amounts, oldAmounts)
      : null;
    if (state.kind === 'sill') buildSillMask(state, sourceData, amounts, oldAmounts);

    const indices = [];
    const values = [];
    const xs = [];
    for (let index = 0; index < amounts.length; index += 1) {
      if (amounts[index] <= 0 && oldAmounts[index] <= 0) continue;
      indices.push(index);
      values.push(amounts[index]);
      xs.push(index % OUTPUT_WIDTH);
    }

    state.maskIndices = Uint32Array.from(indices);
    state.maskAmounts = Float32Array.from(values);
    state.maskOldAmounts = Float32Array.from(indices, index => oldAmounts[index]);
    state.maskXs = Uint16Array.from(xs);
    state.maskCount = indices.length;
    state.letterAmounts = letterAmounts;
    state.maskAmountsByPixel = amounts;
    state.maskOldAmountsByPixel = oldAmounts;
  };

  const transformBadgeStatic = state => {
    if (state.kind !== 'badge') return;
    const data = state.staticData.data;
    const amounts = state.maskAmountsByPixel;
    const letterAmounts = state.letterAmounts;
    for (let index = 0; index < amounts.length; index += 1) {
      const amount = amounts[index];
      if (amount <= 0) continue;
      const offset = index * 4;
      if (letterAmounts?.[index] > 0) {
        data[offset] = clampByte(data[offset] - amount + amount * STATIC_RED[0] / 255);
        data[offset + 1] = clampByte(data[offset + 1] - amount + amount * STATIC_RED[1] / 255);
        data[offset + 2] = clampByte(data[offset + 2] - amount + amount * STATIC_RED[2] / 255);
      } else {
        data[offset] = clampByte(data[offset] + amount * STATIC_RED[0] / 255);
        data[offset + 1] = clampByte(data[offset + 1] + amount * STATIC_RED[1] / 255);
        data[offset + 2] = clampByte(data[offset + 2] + amount * STATIC_RED[2] / 255);
      }
    }
  };

  const buildFlowColors = time => {
    if (flowColorsTime === time) return flowColors;
    flowColorsTime = time;
    const phase = ((time % FLOW_PERIOD) + FLOW_PERIOD) % FLOW_PERIOD / FLOW_PERIOD;
    for (let x = 0; x < OUTPUT_WIDTH; x += 1) {
      const h = (phase + x / OUTPUT_WIDTH) % 1;
      flowColors.red[x] = flowHue(h, 0);
      flowColors.green[x] = flowHue(h, 8);
      flowColors.blue[x] = flowHue(h, 4);
    }
    return flowColors;
  };

  const drawFrame = (state, time) => {
    const colors = buildFlowColors(time);
    const frame = state.frameData;
    frame.data.set(state.staticData.data);
    for (let i = 0; i < state.maskIndices.length; i += 1) {
      const index = state.maskIndices[i];
      const amount = state.maskAmounts[i];
      const oldAmount = state.maskOldAmounts[i] || amount;
      const offset = index * 4;
      const x = state.maskXs[i];
      frame.data[offset] = clampByte(
        state.staticData.data[offset]
          - (state.kind === 'sill' ? oldAmount : amount)
          + amount * colors.red[x] / 255,
      );
      frame.data[offset + 1] = clampByte(
        state.staticData.data[offset + 1] + amount * colors.green[x] / 255,
      );
      frame.data[offset + 2] = clampByte(
        state.staticData.data[offset + 2] + amount * colors.blue[x] / 255,
      );
    }
    state.ctx.putImageData(frame, 0, 0);
    state.currentData = frame;
    state.frameCount += 1;
  };

  const resetCanvas = state => {
    if (!state.staticData || !state.ctx) return;
    state.ctx.putImageData(state.staticData, 0, 0);
    state.currentData = state.staticData;
  };

  const stopFlow = (state, message) => {
    activeStates.delete(state);
    if (activeStates.size === 0 && schedulerFrame !== null) {
      window.cancelAnimationFrame?.(schedulerFrame);
      schedulerFrame = null;
    }
    state.running = false;
    state.owner = null;
    state.hovering = false;
    state.lastFrameTime = -Infinity;
    state.root.dataset.running = 'false';
    if (state.toggle) {
      state.toggle.setAttribute('aria-pressed', 'false');
      state.toggle.textContent = 'Preview Flow';
    }
    resetCanvas(state);
    if (message) setStatus(state, message);
  };

  const scheduleFrames = () => {
    if (schedulerFrame !== null || activeStates.size === 0) return;
    if (typeof window.requestAnimationFrame !== 'function') {
      [...activeStates].forEach(state => stopFlow(state, 'Flow preview is unavailable in this browser.'));
      return;
    }
    schedulerFrame = window.requestAnimationFrame(runFrames);
  };

  const runFrames = time => {
    schedulerFrame = null;
    if (isDocumentHidden()) {
      [...activeStates].forEach(state => stopFlow(state, 'Static red preview.'));
      return;
    }

    for (const state of [...activeStates]) {
      const disconnected = state.root.isConnected === false;
      if (!state.running || !state.ready || disconnected || !state.inViewport) {
        stopFlow(state, 'Static red preview.');
        continue;
      }
      if (state.lastFrameTime === -Infinity || time - state.lastFrameTime >= FRAME_INTERVAL) {
        drawFrame(state, time);
        state.lastFrameTime = time;
      }
    }
    scheduleFrames();
  };

  const startFlow = (state, reason) => {
    if (!state.ready || state.failed || !state.inViewport || isDocumentHidden()) return;
    if (reason === 'hover' && state.owner === 'button') return;
    if (prefersReducedMotion()) {
      stopFlow(state, 'Static red preview; animation disabled by reduced-motion preference.');
      return;
    }
    state.running = true;
    state.owner = reason === 'hover' ? 'hover' : 'button';
    state.hovering = reason === 'hover';
    state.lastFrameTime = -Infinity;
    state.root.dataset.running = 'true';
    state.toggle?.setAttribute('aria-pressed', 'true');
    if (state.toggle) state.toggle.textContent = 'Stop preview';
    setStatus(state, 'Flow preview running.');
    activeStates.add(state);
    scheduleFrames();
  };

  const setUnavailable = (state, message = 'Flow preview unavailable for this image.') => {
    state.failed = true;
    state.ready = false;
    state.root.dataset.ready = 'false';
    state.root.dataset.running = 'false';
    state.toggle?.setAttribute('aria-pressed', 'false');
    if (state.toggle) {
      state.toggle.hidden = true;
      state.toggle.disabled = true;
    }
    setStatus(state, message);
  };

  const makeSourceImage = (state, sourceUrl) => {
    if (!sourceUrl || typeof window.Image !== 'function') {
      setUnavailable(state);
      return;
    }
    const sourceImage = new window.Image();
    sourceImage.crossOrigin = 'anonymous';
    sourceImage.decoding = 'async';
    sourceImage.onload = () => {
      if (state.failed) return;
      if (sourceImage.naturalWidth !== SOURCE_WIDTH || sourceImage.naturalHeight !== SOURCE_HEIGHT) {
        setUnavailable(state, 'Flow preview unavailable: source image dimensions changed.');
        return;
      }
      try {
        const sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = SOURCE_WIDTH;
        sourceCanvas.height = SOURCE_HEIGHT;
        const sourceContext = sourceCanvas.getContext('2d', {willReadFrequently: true});
        if (!sourceContext) throw new Error('2D canvas unavailable');
        sourceContext.drawImage(sourceImage, 0, 0, SOURCE_WIDTH, SOURCE_HEIGHT);
        const sourceData = sourceContext.getImageData(0, 0, SOURCE_WIDTH, SOURCE_HEIGHT);
        const outputData = outputImageData(state, sourceImage, sourceData);
        state.sourceData = sourceData;
        state.staticData = outputData;
        buildMask(state, sourceData);
        transformBadgeStatic(state);
        state.frameData = copyImageData(state.ctx, state.staticData);
        state.currentData = state.staticData;
        state.ctx.putImageData(state.staticData, 0, 0);
        state.ready = true;
        state.root.dataset.ready = 'true';
        state.root.dataset.running = 'false';
        state.toggle.disabled = false;
        state.toggle.hidden = false;
        state.toggle.setAttribute('aria-pressed', 'false');
        state.toggle.textContent = 'Preview Flow';
        setStatus(state, 'Static red preview.');
      } catch (_error) {
        setUnavailable(state, 'Flow preview unavailable: this image cannot be read securely.');
      }
    };
    sourceImage.onerror = () => setUnavailable(state);
    sourceImage.src = sourceUrl;
    state.sourceImage = sourceImage;
  };

  const assertOutsideMaskUnchanged = target => {
    const selected = target ? resolveStates(target) : [...states.values()];
    const results = selected.map(state => {
      if (!state.staticData || !state.ctx) {
        return {kind: state.kind, ready: false, ok: false, checked: 0, differences: 0};
      }
      let current;
      try {
        current = state.ctx.getImageData(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
      } catch (_error) {
        return {kind: state.kind, ready: state.ready, ok: false, checked: 0, differences: 0};
      }
      let checked = 0;
      let differences = 0;
      for (let index = 0; index < state.maskAmountsByPixel.length; index += 1) {
        if (state.maskAmountsByPixel[index] > 0 || state.maskOldAmountsByPixel[index] > 0) continue;
        checked += 1;
        const offset = index * 4;
        for (let channel = 0; channel < 4; channel += 1) {
          if (current.data[offset + channel] !== state.staticData.data[offset + channel]) {
            differences += 1;
            break;
          }
        }
      }
      return {kind: state.kind, ready: state.ready, ok: differences === 0, checked, differences};
    });
    return target ? results[0] || {ready: false, ok: false, checked: 0, differences: 0} : results;
  };

  function resolveStates(target) {
    if (!target) return [...states.values()];
    if (typeof target === 'string') {
      const state = states.get(target);
      return state ? [state] : [];
    }
    if (target && target.nodeType === 1) {
      const state = statesByRoot.get(target);
      return state ? [state] : [];
    }
    if (target.kind && states.get(target.kind) === target) return [target];
    return [];
  }

  const diagnosticSnapshot = target => {
    const selected = target ? resolveStates(target) : [...states.values()];
    const makeSnapshot = state => Object.freeze({
      kind: state.kind,
      status: state.failed ? 'unavailable' : state.ready ? (state.running ? 'running' : 'ready') : 'loading',
      ready: state.ready,
      running: state.running,
      maskcount: state.maskCount,
      framecount: state.frameCount,
      sourcecrop: state.crop.slice(),
      maskCount: state.maskCount,
      frameCount: state.frameCount,
      sourceCrop: state.crop.slice(),
    });
    if (target) return makeSnapshot(selected[0] || {kind: target, failed: true, ready: false, running: false, maskCount: 0, frameCount: 0, crop: []});
    return Object.freeze(Object.fromEntries(selected.map(state => [state.kind, makeSnapshot(state)])));
  };

  const diagnostics = target => diagnosticSnapshot(target);
  Object.defineProperties(diagnostics, {
    get: {value: diagnosticSnapshot},
    snapshot: {value: diagnosticSnapshot},
    assertOutsideMaskUnchanged: {value: assertOutsideMaskUnchanged},
    sill: {get: () => diagnosticSnapshot('sill')},
    badge: {get: () => diagnosticSnapshot('badge')},
    status: {get: () => diagnosticSnapshot()},
    maskcount: {get: () => diagnosticSnapshot()},
    framecount: {get: () => diagnosticSnapshot()},
    sourcecrop: {get: () => diagnosticSnapshot()},
  });
  Object.freeze(diagnostics);
  try {
    if (!Object.prototype.hasOwnProperty.call(window, '__LDS_HUB_BANNER_FLOW__')) {
      Object.defineProperty(window, '__LDS_HUB_BANNER_FLOW__', {
        configurable: false,
        enumerable: false,
        writable: false,
        value: diagnostics,
      });
    }
  } catch (_error) {
    // Diagnostics are optional; a locked host object must not break the banner.
  }

  const init = root => {
    if (!root || statesByRoot.has(root)) return;
    const kind = root.getAttribute('data-hub-banner-flow');
    if (!CONFIG[kind]) return;
    const image = root.querySelector('img');
    const canvas = root.querySelector('canvas');
    const stage = root.querySelector('[data-banner-flow-stage]') || canvas?.parentElement;
    const toggle = root.querySelector('[data-banner-flow-toggle]');
    const status = root.querySelector('[data-banner-flow-status]');
    if (!image || !canvas || !stage || !toggle || !status) return;

    const sourceUrl = image.currentSrc || image.getAttribute('src') || image.src || '';
    const state = {
      root,
      kind,
      crop: CONFIG[kind].crop.slice(),
      image,
      canvas,
      stage,
      toggle,
      status,
      sourceUrl,
      ctx: null,
      sourceImage: null,
      sourceData: null,
      staticData: null,
      frameData: null,
      currentData: null,
      maskIndices: new Uint32Array(0),
      maskAmounts: new Float32Array(0),
      maskOldAmounts: new Float32Array(0),
      maskXs: new Uint16Array(0),
      maskAmountsByPixel: new Float32Array(OUTPUT_WIDTH * OUTPUT_HEIGHT),
      maskOldAmountsByPixel: new Float32Array(OUTPUT_WIDTH * OUTPUT_HEIGHT),
      letterAmounts: null,
      maskCount: 0,
      frameCount: 0,
      ready: false,
      failed: false,
      running: false,
      owner: null,
      hovering: false,
      inViewport: true,
      lastFrameTime: -Infinity,
      observer: null,
    };
    states.set(kind, state);
    statesByRoot.set(root, state);
    root.dataset.ready = 'false';
    root.dataset.running = 'false';
    canvas.width = OUTPUT_WIDTH;
    canvas.height = OUTPUT_HEIGHT;
    canvas.setAttribute('aria-hidden', 'true');
    toggle.type = 'button';
    toggle.setAttribute('aria-pressed', 'false');
    toggle.disabled = true;
    toggle.textContent = 'Preview Flow';
    setStatus(state, 'Loading preview.');

    try {
      state.ctx = canvas.getContext('2d', {willReadFrequently: true});
      if (!state.ctx) throw new Error('2D canvas unavailable');
    } catch (_error) {
      setUnavailable(state, 'Flow preview unavailable in this browser.');
      return;
    }

    if (!sourceMatches(kind, sourceUrl)) {
      setUnavailable(state);
      return;
    }

    const onPointerEnter = event => {
      if (event?.pointerType === 'touch' || !hoverFine()) return;
      if (state.owner === 'button') return;
      state.hovering = true;
      startFlow(state, 'hover');
    };
    const onPointerLeave = () => {
      if (state.owner !== 'hover') return;
      stopFlow(state, 'Static red preview.');
    };
    stage.addEventListener('pointerenter', onPointerEnter);
    stage.addEventListener('pointerleave', onPointerLeave);
    stage.addEventListener('mouseenter', onPointerEnter);
    stage.addEventListener('mouseleave', onPointerLeave);
    toggle.addEventListener('click', () => {
      if (!state.ready) return;
      if (state.running) {
        stopFlow(state, 'Static red preview.');
      } else {
        startFlow(state, 'button');
      }
    });

    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    motionQuery?.addEventListener?.('change', event => {
      if (event.matches) stopFlow(state, 'Static red preview; animation disabled by reduced-motion preference.');
    });
    document.addEventListener('visibilitychange', () => {
      if (isDocumentHidden()) stopFlow(state, 'Static red preview.');
    });

    if (typeof window.IntersectionObserver === 'function') {
      state.observer = new window.IntersectionObserver(entries => {
        const entry = entries[0];
        state.inViewport = Boolean(entry?.isIntersecting);
        if (!state.inViewport) stopFlow(state, 'Static red preview.');
      }, {threshold: 0});
      state.observer.observe(root);
    }

    makeSourceImage(state, sourceUrl);
  };

  const initAll = () => document.querySelectorAll(FLOW_SELECTOR).forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, {once: true});
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
